import { z } from "zod";
import { pageQuerySchema, sortSchema } from "./pagination";

/**
 * Where a student is in the school's life: enquiry and applied are before admission (not in
 * class lists), admitted is accepted but not yet attending, active is studying now, and
 * inactive, withdrawn and alumni are after.
 */
export const STUDENT_STATUSES = [
  "enquiry",
  "applied",
  "admitted",
  "active",
  "inactive",
  "withdrawn",
  "alumni",
] as const;
export type StudentStatus = (typeof STUDENT_STATUSES)[number];

export const STUDENT_STATUS_LABELS: Record<StudentStatus, string> = {
  enquiry: "Enquiry",
  applied: "Applied",
  admitted: "Admitted",
  active: "Active",
  inactive: "Inactive",
  withdrawn: "Withdrawn",
  alumni: "Alumni",
};

/**
 * Where a student's family is with the app: not invited, invited (filling in), submitted
 * (waiting for the school), approved (the app is open), or sent back with a note.
 */
export const PORTAL_STATUSES = ["none", "invited", "submitted", "approved", "sent_back"] as const;
export type PortalStatus = (typeof PORTAL_STATUSES)[number];

export const PORTAL_STATUS_LABELS: Record<PortalStatus, string> = {
  none: "Not invited",
  invited: "Invited",
  submitted: "Waiting for the school",
  approved: "Using the app",
  sent_back: "Sent back to the family",
};

/** Statuses the office chooses when adding a student (enquiries come from the online form). */
export const NEW_STUDENT_STATUSES = ["applied", "admitted", "active"] as const;

export const GENDERS = ["female", "male", "other"] as const;
export type Gender = (typeof GENDERS)[number];

export const GENDER_LABELS: Record<Gender, string> = {
  female: "Girl",
  male: "Boy",
  other: "Other",
};

const MIN_AGE_YEARS = 2;
const MAX_AGE_YEARS = 25;

function ageInYears(isoDate: string, today: Date): number {
  const born = new Date(`${isoDate}T00:00:00Z`);
  let age = today.getUTCFullYear() - born.getUTCFullYear();
  const beforeBirthday =
    today.getUTCMonth() < born.getUTCMonth() ||
    (today.getUTCMonth() === born.getUTCMonth() && today.getUTCDate() < born.getUTCDate());
  if (beforeBirthday) age -= 1;
  return age;
}

export const dateOfBirthSchema = z
  .string()
  .trim()
  .min(1, { error: "Enter the date of birth", abort: true })
  .pipe(z.iso.date({ error: "Enter the date of birth as YYYY-MM-DD" }))
  .refine(
    (value) => {
      const age = ageInYears(value, new Date());
      // A malformed date already has its own message; don't add an age error on top.
      if (Number.isNaN(age)) return true;
      return age >= MIN_AGE_YEARS && age <= MAX_AGE_YEARS;
    },
    { error: `Student must be between ${MIN_AGE_YEARS} and ${MAX_AGE_YEARS} years old` },
  );

/**
 * Indian mobile number. Accepts "+91 98765 43210", "098765-43210" or "9876543210"
 * and stores the 10-digit form.
 */
export const indianMobileSchema = z
  .string()
  .trim()
  .transform((value) => value.replace(/[\s()-]/g, "").replace(/^(\+91|0091|91(?=\d{10}$)|0)/, ""))
  .pipe(z.string().regex(/^[6-9]\d{9}$/, { error: "Enter a 10-digit mobile number" }));

export const admissionNoSchema = z
  .string()
  .trim()
  .toUpperCase()
  .min(1, { error: "Enter the admission number", abort: true })
  .max(32, { error: "Admission number is too long" })
  .regex(/^[A-Za-z0-9/-]+$/, { error: "Use letters, numbers, / or -" });

/** The always-asked fields, without defaults, so a partial update never fills one in. */
export const studentCoreFields = z.object({
  admissionNo: admissionNoSchema,
  firstName: z
    .string()
    .trim()
    .min(1, { error: "Enter the first name" })
    .max(80, { error: "First name is too long" }),
  lastName: z.string().trim().max(80, { error: "Last name is too long" }),
  dateOfBirth: dateOfBirthSchema,
  gender: z.enum(GENDERS, { error: "Choose a gender" }),
  sectionId: z.string().trim().min(1, { error: "Choose a class and section" }),
});

/** Columns the student table sorts by. "class" follows the school's order of sections. */
export const STUDENT_SORTS = [
  "name",
  "admissionNo",
  "class",
  "dateOfBirth",
  "status",
  "createdAt",
] as const;
export type StudentSort = (typeof STUDENT_SORTS)[number];

const studentFilters = {
  q: z.string().trim().max(80).optional(),
  sectionId: z.string().trim().max(64).optional(),
  status: z.enum(STUDENT_STATUSES).optional(),
  /** Where the family is with the app (e.g. details waiting for the school). */
  portal: z.enum(PORTAL_STATUSES).optional(),
  sort: sortSchema(STUDENT_SORTS, "name"),
};

export const studentListQuerySchema = pageQuerySchema.extend(studentFilters);

/** A download of everything the table would list, in its order. */
export const studentExportQuerySchema = z.object(studentFilters);

/** Columns of the student download. Parents' numbers are never in it. */
export const STUDENT_EXPORT_COLUMNS = [
  "Admission no",
  "First name",
  "Last name",
  "Class",
  "Date of birth",
  "Gender",
  "Status",
  "Added on",
] as const;

export type StudentListQuery = z.output<typeof studentListQuerySchema>;
export type StudentExportQuery = z.output<typeof studentExportQuerySchema>;

export interface StudentDto {
  id: string;
  admissionNo: string;
  firstName: string;
  lastName: string;
  fullName: string;
  dateOfBirth: string;
  gender: Gender;
  sectionId: string;
  sectionName: string;
  /** The main contact's mobile, masked as "••••••3210" unless the caller may see contact details. */
  guardianPhone: string;
  guardianPhoneMasked: boolean;
  status: StudentStatus;
  admissionDate: string | null;
  /** When the photo last changed (its version for caching); null without a photo. */
  photoUpdatedAt: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  /** What the signed-in person may do with this record (Cerbos decided), for its buttons. */
  can: { update: boolean; delete: boolean };
}

export interface SectionDto {
  id: string;
  grade: string;
  name: string;
  label: string;
}

/** Masked mobile from its last four digits: "••••••3210". */
export function maskPhoneLast4(last4: string): string {
  return `${"•".repeat(6)}${last4}`;
}

export function maskPhone(phone: string): string {
  return phone.length <= 4 ? "••••" : `${"•".repeat(phone.length - 4)}${phone.slice(-4)}`;
}
